/**
 * AI Cost Analytics Service
 * Cross-tenant AI cost dashboard data (management-scoped).
 * Reads the pre-aggregated rollup via the backend cost-analytics API.
 */

import apiClient from './api';
import { API_ENDPOINTS } from '@/config/api.config';

export type TrendGranularity = 'daily' | 'weekly' | 'monthly';

export interface CostAnalyticsFilter {
  start_date?: string;        // 'YYYY-MM-DD'
  end_date?: string;          // 'YYYY-MM-DD'
  tenant_keys?: string[];
  service_categories?: string[];
  models?: string[];
  feature_types?: string[];
}

export interface CostTotals {
  total_cost: number;
  request_count: number;
  total_tokens: number;
  prompt_tokens: number;
  completion_tokens: number;
}

export interface CostOverview extends CostTotals {
  avg_cost_per_request: number;
  previous: CostTotals;
  cost_change_pct: number | null;
}

export interface TenantCostRow {
  tenant_key: string;
  tenant_name: string;
  total_cost: number;
  request_count: number;
  total_tokens: number;
  prompt_tokens: number;
  completion_tokens: number;
}

export interface ModelCostRow {
  model: string;
  service_category: string;
  total_cost: number;
  request_count: number;
  total_tokens: number;
  prompt_tokens: number;
  completion_tokens: number;
}

export interface CategoryCostRow {
  service_category: string;
  total_cost: number;
  request_count: number;
  total_tokens: number;
}

export interface FeatureCostRow {
  feature_type: string;
  total_cost: number;
  request_count: number;
  total_tokens: number;
  prompt_tokens: number;
  completion_tokens: number;
  item_count?: number;          // only present when a single tenant is selected
  avg_cost_per_item?: number;   // unit economics — only for single tenant
}

export interface TrendPoint {
  period: string;
  total_cost: number;
  request_count: number;
  total_tokens: number;
  prompt_tokens?: number;      
  completion_tokens?: number; 
}

export interface TenantTrendPoint extends TrendPoint {
  tenant_key: string;
  tenant_name: string;
}

export interface CostDetailItem {
  reference_id: string;
  name: string;
  feature_type: string;
  total_cost: number;
  total_tokens: number;
  prompt_tokens: number;
  completion_tokens: number;
  cost_by_service: Record<string, { cost?: number; call_count?: number; usage?: number; unit?: string }>;
  cost_by_stage: Record<string, { cost?: number; call_count?: number }>;
  date: string;
}

export interface CostDetailResult {
  items: CostDetailItem[];
  total: number;
}

export interface ExportLogItem {
  user_name?: string;
  user_email?: string;
  user_id?: string;
  start_date?: string;
  end_date?: string;
  tenant_keys?: string[];
  service_categories?: string[];
  pdf_url: string;
  createdAt: string;
}

export interface ExportLogResult {
  items: ExportLogItem[];
  total: number;
}

export interface RollupRunSummary {
  mode: 'full' | 'incremental';
  total_tenants: number;
  processed: number;
  skipped: number;
  failed: number;
  buckets_written: number;
  duration_ms: number;
}

class CostAnalyticsService {
  async getOverview(filter: CostAnalyticsFilter): Promise<CostOverview> {
    const response = await apiClient.post<{ success: boolean; data: CostOverview }>(
      API_ENDPOINTS.COST_ANALYTICS.OVERVIEW,
      filter,
    );
    if (!response.data.success) throw response.data;
    return response.data.data;
  }

  async getByTenant(filter: CostAnalyticsFilter): Promise<TenantCostRow[]> {
    const response = await apiClient.post<{ success: boolean; data: TenantCostRow[] }>(
      API_ENDPOINTS.COST_ANALYTICS.BY_TENANT,
      filter,
    );
    if (!response.data.success) throw response.data;
    return response.data.data;
  }

  async getByModel(filter: CostAnalyticsFilter): Promise<ModelCostRow[]> {
    const response = await apiClient.post<{ success: boolean; data: ModelCostRow[] }>(
      API_ENDPOINTS.COST_ANALYTICS.BY_MODEL,
      filter,
    );
    if (!response.data.success) throw response.data;
    return response.data.data;
  }

  async getByCategory(filter: CostAnalyticsFilter): Promise<CategoryCostRow[]> {
    const response = await apiClient.post<{ success: boolean; data: CategoryCostRow[] }>(
      API_ENDPOINTS.COST_ANALYTICS.BY_CATEGORY,
      filter,
    );
    if (!response.data.success) throw response.data;
    return response.data.data;
  }

  /** Cost per feature (Course / Certification / Document Store / …) + unit economics for a single tenant. */
  async getByFeature(filter: CostAnalyticsFilter): Promise<FeatureCostRow[]> {
    const response = await apiClient.post<{ success: boolean; data: FeatureCostRow[] }>(
      API_ENDPOINTS.COST_ANALYTICS.BY_FEATURE,
      filter,
    );
    if (!response.data.success) throw response.data;
    return response.data.data;
  }

  async getTrend(filter: CostAnalyticsFilter, granularity: TrendGranularity = 'daily'): Promise<TrendPoint[]> {
    const response = await apiClient.post<{ success: boolean; data: TrendPoint[] }>(
      API_ENDPOINTS.COST_ANALYTICS.TREND,
      { ...filter, granularity },
    );
    if (!response.data.success) throw response.data;
    return response.data.data;
  }

  async getTrendByTenant(filter: CostAnalyticsFilter, granularity: TrendGranularity = 'daily'): Promise<TenantTrendPoint[]> {
    const response = await apiClient.post<{ success: boolean; data: TenantTrendPoint[] }>(
      API_ENDPOINTS.COST_ANALYTICS.TREND_BY_TENANT,
      { ...filter, granularity },
    );
    if (!response.data.success) throw response.data;
    return response.data.data;
  }

  /** Per-item cost detail for one tenant (drill-down: each course/cert/… by name + cost). */
  async getTenantCostDetail(
    tenantKey: string,
    filter: CostAnalyticsFilter,
    opts: { skip?: number; limit?: number; sort?: 'cost' | 'date'; order?: 'asc' | 'desc' } = {},
  ): Promise<CostDetailResult> {
    const response = await apiClient.post<{ success: boolean; data: CostDetailResult }>(
      API_ENDPOINTS.COST_ANALYTICS.TENANT_DETAIL,
      { ...filter, tenant_key: tenantKey, skip: opts.skip ?? 0, limit: opts.limit ?? 20, sort: opts.sort, order: opts.order },
    );
    if (!response.data.success) throw response.data;
    return response.data.data;
  }

  /** Generate a branded PDF report server-side, upload to S3, and return its public URL. */
  async exportPdf(filter: CostAnalyticsFilter, cost_format?: 'dollar' | 'credit'): Promise<{ url: string }> {
    const response = await apiClient.post<{ success: boolean; data: { url: string } }>(
      API_ENDPOINTS.COST_ANALYTICS.EXPORT_PDF,
      { ...filter, cost_format },   // display format for the PDF (backend converts $ → credits)
    );
    if (!response.data.success) throw response.data;
    return response.data.data;
  }

  /** Paginated PDF-export history (who downloaded, when, scope, url). */
  async getExportLog(opts: { skip?: number; limit?: number } = {}): Promise<ExportLogResult> {
    const response = await apiClient.post<{ success: boolean; data: ExportLogResult }>(
      API_ENDPOINTS.COST_ANALYTICS.EXPORT_LOG,
      { skip: opts.skip ?? 0, limit: opts.limit ?? 20 },
    );
    if (!response.data.success) throw response.data;
    return response.data.data;
  }

  /** Manually trigger the rollup (pass full=true for a historical backfill). */
  async runRollup(full = false): Promise<RollupRunSummary> {
    const response = await apiClient.post<{ success: boolean; data: RollupRunSummary }>(
      API_ENDPOINTS.COST_ANALYTICS.RUN_ROLLUP,
      { full },
    );
    if (!response.data.success) throw response.data;
    return response.data.data;
  }
}

export default new CostAnalyticsService();
